import { rpc } from './supabase';
import type {
  Draft,
  MutationResult,
  NoticeRow,
  Preview,
  Session,
  Stats,
} from './types';

/**
 * Le sette RPC che il browser dell'operatore può chiamare. Non ce ne sono
 * altre: tutto il resto dello schema è irraggiungibile da qui, e va bene così.
 *
 * `test_comune_notice` esiste ancora lato server ed è ancora concessa a
 * `authenticated`, ma la console non la chiama più (14/08/2026): «invia solo a
 * me» manda ai device di `auth.uid()`, e un account creato dal pannello per la
 * console non ne ha nessuno — il bottone dava un ✓ verde e non squillava
 * niente. Il grant resta perché toglierlo è una migration che non serve a
 * nessuno; a mancare è la superficie che lo chiamava.
 *
 * Nessuna di queste funzioni "prepara" i dati per il server: passa quello che
 * l'operatore ha scritto e lascia che sia il server a validare, comporre e
 * rifiutare. Una console che pre-valida per essere gentile finisce sempre col
 * pre-validare in modo diverso dal server, e allora l'anteprima mente.
 */

export const getSession = () => rpc<Session | null>('municipal_session');

export const getNotices = (istat: string, limit = 50) =>
  rpc<NoticeRow[]>('municipal_notices', { p_istat: istat, p_limit: limit });

export const getStats = (istat: string, id: string) =>
  rpc<Stats>('municipal_notice_stats', { p_istat: istat, p_id: id });

/** I parametri comuni a preview / publish: una sola forma, un solo posto. */
function draftArgs(istat: string, d: Draft) {
  return {
    p_istat: istat,
    p_title: d.title,
    p_body: d.body,
    p_link_url: d.linkUrl.trim() || null,
    p_link_label: d.linkLabel.trim() || null,
    /* La notifica critica viaggia come `emergenza`: è il solo valore oltre
       ad `avviso` che il vincolo della colonna accetta, e finisce sia sulla
       riga sia nel payload del comando `dispatch_comune_notice`, dove il
       dispatcher lo leggerà (vedi lib/types.ts e lib/features.ts). Senza push
       non c'è niente da rendere critico: si torna ad `avviso`, così la riga
       agli atti non dichiara un'urgenza che nessun telefono ha ricevuto.
       Passato esplicito invece che omesso perché il default della funzione
       SQL è un dettaglio della funzione SQL, e questa riga deve dire da sola
       cosa parte. */
    p_urgency: d.push && d.critical ? 'emergenza' : 'avviso',
    p_expires_at: d.expiresAt || null,
    p_push: d.push,
    p_revokes_id: d.revokesId,
  };
}

/**
 * Valida e mostra, senza pubblicare.
 *
 * Restituisce le stringhe già composte dal server: la schermata di conferma
 * renderizza quelle, mai una ricostruzione locale. È l'unico modo perché
 * «stai per notificare 4.212 persone» sia una garanzia e non una promessa.
 */
export const preview = (istat: string, d: Draft) =>
  rpc<Preview>('preview_comune_notice', draftArgs(istat, d));

/**
 * Pubblica.
 *
 * Rivalida tutto lato server: fra l'anteprima e il click possono passare
 * minuti, e in quei minuti la quota può essersi esaurita o può essere iniziata
 * la fascia notturna. Un `ok: false` qui non è un bug della console.
 *
 * `tentativo` è la chiave di idempotenza, ed è il solo argomento che questa
 * console inventa invece di limitarsi a passare quello che l'operatore ha
 * scritto. Ripeterla non pubblica una seconda volta: il server restituisce
 * l'avviso già creato, con `duplicate: true`. È ciò che rende innocuo il caso
 * che capita davvero — la risposta persa e il secondo clic — e senza cui la
 * seconda chiamata manderebbe una seconda notifica a tutto il Comune.
 *
 * Omessa (bozza salvata da una versione precedente) si pubblica come prima: il
 * parametro ha un default lato server.
 */
export const publish = (istat: string, d: Draft, tentativo?: string) =>
  rpc<MutationResult>('publish_comune_notice', {
    ...draftArgs(istat, d),
    p_idempotency_key: tentativo ?? null,
  });

/** Ritiro: l'avviso sparisce dall'app, resta agli atti. Nessuna push. */
export const retract = (istat: string, id: string) =>
  rpc<MutationResult>('retract_comune_notice', { p_istat: istat, p_id: id });

/** Proroga: sposta la scadenza. Non notifica e non consuma quota. */
export const extend = (istat: string, id: string, expiresAt: string) =>
  rpc<MutationResult>('extend_comune_notice', {
    p_istat: istat,
    p_id: id,
    p_expires_at: expiresAt,
  });
